/**
 * Auth Service - API calls para autenticación y suscripciones
 */
import axios from 'axios';

// Detectar URL dinámica del backend
const getBackendURL = () => {
    // Prioridad 1: variable de entorno (VITE_API_URL incluye /api al final, la quitamos)
    if (import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL.replace(/\/api$/, '');
    }
    const hostname = window.location.hostname;
    const isPrivate = hostname === 'localhost' || hostname === '127.0.0.1' ||
        /^192\.168\./.test(hostname) || /^10\./.test(hostname) ||
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname);
    const port = import.meta.env.VITE_BACKEND_PORT || '63000';
    return isPrivate
        ? `http://${hostname}:${port}`
        : `${window.location.protocol}//${window.location.hostname}`;
};

const API_BASE_URL = getBackendURL();

// Crear instancia de axios con interceptor para JWT
const authAxios = axios.create({
    baseURL: API_BASE_URL
});

// Interceptor para agregar JWT a todas las peticiones
authAxios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor para manejar errores de autenticación
authAxios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userData');
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
        return Promise.reject(error);
    }
);

export const AuthService = {
    /**
     * Registro de nuevo usuario
     * @param {Object} userData - { firstName, lastName, email, password }
     * @returns {Promise<{success: boolean, user: Object, message: string}>}
     */
    async signup(userData) {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/session/signup`, userData);
            return {
                success: true,
                user: response.data.user,
                message: response.data.message
            };
        } catch (error) {
            console.error('[AUTH] Signup error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Error al registrar usuario'
            };
        }
    },

    /**
     * Login de usuario
     * @param {Object} credentials - { email, password }
     * @returns {Promise<{success: boolean, token: string, user: Object}>}
     */
    async login(credentials) {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/session/login`, credentials);
            
            if (response.data.success) {
                // Guardar token y datos de usuario en localStorage
                localStorage.setItem('authToken', response.data.token);
                localStorage.setItem('userData', JSON.stringify(response.data.user));
                
                return {
                    success: true,
                    token: response.data.token,
                    user: response.data.user
                };
            }
            
            return {
                success: false,
                error: response.data.error || 'Error al iniciar sesión'
            };
        } catch (error) {
            console.error('[AUTH] Login error:', error);
            
            // Manejar errores específicos
            if (error.response?.data?.emailNotVerified) {
                return {
                    success: false,
                    emailNotVerified: true,
                    error: 'Por favor verifica tu email antes de iniciar sesión'
                };
            }
            
            return {
                success: false,
                error: error.response?.data?.error || 'Error al iniciar sesión'
            };
        }
    },

    /**
     * Cerrar sesión
     */
    logout() {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        window.location.href = '/login';
    },

    /**
     * Confirmar email con token
     * @param {string} token - Token de confirmación
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async confirmEmail(token) {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/session/confirm-email?token=${token}`);
            return {
                success: true,
                message: response.data.message,
                alreadyVerified: response.data.alreadyVerified
            };
        } catch (error) {
            console.error('[AUTH] Confirm email error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Error al confirmar email',
                expired: error.response?.data?.expired
            };
        }
    },

    /**
     * Reenviar email de confirmación
     * @param {string} email - Email del usuario
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async resendConfirmationEmail(email) {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/session/resend-confirmation`, { email });
            return {
                success: true,
                message: response.data.message
            };
        } catch (error) {
            console.error('[AUTH] Resend confirmation error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Error al reenviar email de confirmación',
                alreadyVerified: error.response?.data?.alreadyVerified
            };
        }
    },

    /**
     * Obtener usuario actual desde localStorage
     * @returns {Object|null}
     */
    getCurrentUser() {
        try {
            const userData = localStorage.getItem('userData');
            return userData ? JSON.parse(userData) : null;
        } catch (error) {
            console.error('[AUTH] Error parsing user data:', error);
            return null;
        }
    },

    /**
     * Verificar si el usuario está autenticado
     * @returns {boolean}
     */
    isAuthenticated() {
        const token = localStorage.getItem('authToken');
        return !!token;
    },

    /**
     * Obtener información del plan actual del usuario
     * @returns {Promise<{success: boolean, subscription: Object}>}
     */
    async getSubscriptionInfo() {
        try {
            const response = await authAxios.get('/api/subscription/info');
            return {
                success: true,
                subscription: response.data.subscription
            };
        } catch (error) {
            console.error('[AUTH] Get subscription error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Error al obtener información de suscripción'
            };
        }
    },

    /**
     * Obtener planes disponibles
     * @returns {Promise<{success: boolean, plans: Array}>}
     */
    async getAvailablePlans() {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/plans/available`);
            return {
                success: true,
                plans: response.data.plans
            };
        } catch (error) {
            console.error('[AUTH] Get plans error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Error al obtener planes'
            };
        }
    }
};

export default AuthService;
